import { useEffect, useRef } from 'react';

const stats = [
  { icon: 'ri-eye-line', value: '12,847', label: 'Secrets Discovered' },
  { icon: 'ri-gamepad-line', value: '8,392', label: 'Active Players' },
  { icon: 'ri-flashlight-line', value: '247', label: 'New This Week' },
];

export default function HeroSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    let frame: number;
    let glitching = false;

    const glitch = () => {
      if (glitching) return;
      glitching = true;
      el.style.textShadow = '2px 0 #a855f7, -2px 0 #facc15';
      setTimeout(() => {
        el.style.textShadow = '0 0 30px rgba(168,85,247,0.8), 0 0 60px rgba(168,85,247,0.4)';
        glitching = false;
      }, 120);
    };

    const interval = setInterval(glitch, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#0a0a0f]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/236e51aacbfc39ae452419bf55d9d16a.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/60 via-[#0a0a0f]/40 to-[#0a0a0f]"></div>
        <div className="absolute inset-0 bg-gradient-radial from-purple-900/30 via-transparent to-transparent"></div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full opacity-30"
            style={{
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              background: i % 2 === 0 ? '#a855f7' : '#facc15',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 6 + 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 4}s`,
              boxShadow: i % 2 === 0 ? '0 0 8px #a855f7' : '0 0 8px #facc15',
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-6 text-center flex flex-col items-center">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-purple-500/60 mb-6 md:mb-8"
          style={{ background: 'rgba(168,85,247,0.15)', boxShadow: '0 0 16px rgba(168,85,247,0.3)' }}
        >
          <span className="text-yellow-400 text-xs font-black uppercase tracking-widest">🔥 Trending Now</span>
        </div>

        {/* Main Title */}
        <h1
          ref={titleRef}
          className="font-black text-white leading-tight mb-4 md:mb-6 transition-all duration-100"
          style={{
            fontSize: 'clamp(2.2rem, 7vw, 5.5rem)',
            textShadow: '0 0 30px rgba(168,85,247,0.8), 0 0 60px rgba(168,85,247,0.4)',
            fontFamily: "'Orbitron', sans-serif",
          }}
        >
          We Found Secrets<br />
          You Shouldn&apos;t See… 😳
        </h1>

        {/* Subtitle */}
        <p
          className="text-yellow-400 font-semibold mb-8 md:mb-10 max-w-2xl"
          style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.35rem)',
            textShadow: '0 0 12px rgba(250,204,21,0.5)',
          }}
        >
          Hidden rooms, secret tricks and crazy Roblox discoveries
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full">
          <a
            href="#products"
            className="flex items-center gap-2 bg-yellow-400 text-black font-black uppercase tracking-wider px-8 py-4 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-yellow-300 hover:scale-105 w-full sm:w-auto justify-center"
            style={{ boxShadow: '0 0 30px rgba(250,204,21,0.6), 0 0 60px rgba(250,204,21,0.2)', fontSize: '1rem' }}
          >
            <i className="ri-lock-unlock-line text-lg"></i>
            Unlock Secrets Now
          </a>
          <a
            href="#membership"
            className="flex items-center gap-2 bg-transparent text-white font-black uppercase tracking-wider px-8 py-4 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-purple-600/30 w-full sm:w-auto justify-center border-2 border-purple-500"
            style={{ boxShadow: '0 0 20px rgba(168,85,247,0.4)', fontSize: '1rem' }}
          >
            <i className="ri-lock-2-line text-lg"></i>
            Join the Secret Club
          </a>
        </div>

        {/* Stats */}
        <div className="mt-12 md:mt-16 grid grid-cols-3 gap-3 md:gap-6 w-full max-w-2xl">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col items-center gap-1 px-3 py-3 md:py-4 rounded-xl border border-purple-800/50"
              style={{ background: 'rgba(168,85,247,0.08)', backdropFilter: 'blur(8px)' }}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${stat.icon} text-yellow-400 text-xl`} style={{ filter: 'drop-shadow(0 0 6px rgba(250,204,21,0.8))' }}></i>
              </div>
              <span
                className="text-yellow-400 font-black text-lg md:text-2xl"
                style={{ textShadow: '0 0 10px rgba(250,204,21,0.6)' }}
              >
                {stat.value}
              </span>
              <span className="text-gray-400 text-xs text-center leading-tight">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-gray-500 text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-purple-500 text-lg"></i>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        .bg-gradient-radial {
          background: radial-gradient(ellipse at center, var(--tw-gradient-stops));
        }
      `}</style>
    </section>
  );
}
